import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import type { CarStats, CarTuning } from '../cars/CarTypes';
import { RealisticCarRenderer } from '../../engine/RealisticCarRenderer';
import {
  loadCarRaceAssetManifest,
  normalizeCarAssetUrls
} from '../../assets/AssetConfig';

export class Car {
  readonly group = new THREE.Group();
  readonly body: THREE.Mesh;
  readonly wheels: THREE.Mesh[] = [];
  readonly stats: CarStats;
  private proceduralCar: THREE.Object3D | null = null;

  position = new THREE.Vector3(0, 0.6, 0);
  heading = 0;
  velocity = new THREE.Vector3();
  speed = 0;
  steer = 0;

  constructor(stats: CarStats, tuning?: CarTuning) {
    this.stats = stats;

    // Verwende realistisches Auto-Rendering
    const colorHex = tuning?.colorHex ?? '#1580ff';
    const neonColorHex = tuning?.neonColorHex ?? '#2ee6ff';
    const neonIntensity = tuning?.neonIntensity ?? 0.18;

    const carMesh = RealisticCarRenderer.createCarMesh(stats, colorHex, neonColorHex, neonIntensity);
    this.proceduralCar = carMesh;
    this.group.add(carMesh);

    // Finde Reifen und Body für Kompatibilität
    this.body = new THREE.Mesh(
      new THREE.BoxGeometry(2, 1.2, 4.5),
      new THREE.MeshStandardMaterial({ color: colorHex })
    );
    this.body.position.y = 0.6;

    // Sammle alle Wheels
    carMesh.children.forEach((child) => {
      if (child instanceof THREE.Mesh && child.geometry instanceof THREE.CylinderGeometry) {
        this.wheels.push(child);
      }
    });

    this.loadExternalCarModel(stats.id);

    this.syncTransform();
  }

  syncTransform() {
    this.group.position.copy(this.position);
    this.group.rotation.set(0, this.heading, 0);
  }

  private loadExternalCarModel(carId: string) {
    const loader = new GLTFLoader();

    const setupMesh = (root: THREE.Object3D) => {
      root.traverse((node) => {
        const mesh = node as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          mesh.frustumCulled = true;
        }
      });
      root.scale.setScalar(1.35);
      root.position.y = 0.02;
      // Rotation entfernt: Auto fährt jetzt korrekt vorwärts
    };

    const hasRenderableMesh = (model: THREE.Object3D): boolean => {
      let found = false;
      model.traverse((node) => {
        if ((node as THREE.Mesh).isMesh) {
          found = true;
        }
      });
      return found;
    };

    const useModel = (model: THREE.Object3D) => {
      if (!hasRenderableMesh(model)) return;
      if (this.proceduralCar) {
        this.group.remove(this.proceduralCar);
        this.proceduralCar = null;
      }
      this.group.add(model);
    };

    const pickVariantFromScene = (scene: THREE.Object3D): THREE.Object3D => {
      const variantIndex = this.stats.modelVariantIndex;
      if (variantIndex === undefined || variantIndex < 0) {
        return scene;
      }

      const candidates = scene.children.filter((child) => {
        if ((child as THREE.Mesh).isMesh) return true;
        return child.children.some((nested) => (nested as THREE.Mesh).isMesh);
      });

      if (candidates.length === 0) {
        return scene;
      }

      const selected = candidates[variantIndex % candidates.length].clone(true);
      const wrapper = new THREE.Group();
      wrapper.add(selected);
      return wrapper;
    };

    const loadSingle = (url: string) =>
      new Promise<THREE.Object3D>((resolve, reject) => {
        loader.load(
          url,
          (gltf) => resolve(gltf.scene),
          undefined,
          reject
        );
      });

    (async () => {
      try {
        const manifest = await loadCarRaceAssetManifest();
        const manifestEntry = manifest.cars?.[carId];
        const urls = normalizeCarAssetUrls(manifestEntry, carId);
        const lodDistances = {
          high: manifest.performance?.highLodDistance ?? 0,
          mid: manifest.performance?.midLodDistance ?? 35,
          low: manifest.performance?.lowLodDistance ?? 70
        };

        const hasExplicitLod = Boolean(manifestEntry?.high && manifestEntry?.mid && manifestEntry?.low);

        // Fallback-First-Strategie wenn keine LOD-Varianten im Manifest
        if (!hasExplicitLod) {
          try {
            const single = await loadSingle(urls.fallback);
            const variantScene = pickVariantFromScene(single);
            setupMesh(variantScene);
            useModel(variantScene);
            return;
          } catch {
            // Fallback bleibt prozedurales Modell
            return;
          }
        }

        // LOD-Varianten laden
        const [high, mid, low] = await Promise.all([
          loadSingle(urls.high).catch(() => null),
          loadSingle(urls.mid).catch(() => null),
          loadSingle(urls.low).catch(() => null)
        ]);

        if (high && mid && low) {
          setupMesh(high);
          setupMesh(mid);
          setupMesh(low);

          const lod = new THREE.LOD();
          lod.addLevel(high, lodDistances.high);
          lod.addLevel(mid, lodDistances.mid);
          lod.addLevel(low, lodDistances.low);
          useModel(lod);
          return;
        }

        // Fallback wenn LOD-Laden fehlgeschlagen
        try {
          const single = await loadSingle(urls.fallback);
          const variantScene = pickVariantFromScene(single);
          setupMesh(variantScene);
          useModel(variantScene);
        } catch {
          // Fallback bleibt prozedurales Modell
        }
      } catch {
        // Fallback bleibt prozedurales Modell
      }
    })();
  }
}

